import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ControlBadge } from "@/components/ControlBadge";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [settings, setSettings] = useState({ org_name: "", tagline: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { org_name: settings.org_name, tagline: settings.tagline });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#252221]">Settings</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Organization and account preferences.</p>
      </div>

      <Card className="p-6 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex items-center gap-3 mb-5">
          <Logo size={44} />
          <div>
            <div className="font-display font-semibold text-[#252221]">Organization</div>
            <div className="text-xs text-muted-foreground">Branding shown across the Command Center</div>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 flex items-center gap-2 text-[13px] font-medium">Organization Name <ControlBadge kind="global" /></Label>
            <Input value={settings.org_name || ""} onChange={(e) => setSettings((s) => ({ ...s, org_name: e.target.value }))} disabled={!isAdmin} data-testid="settings-org-name" />
          </div>
          <div>
            <Label className="mb-1.5 flex items-center gap-2 text-[13px] font-medium">Tagline <ControlBadge kind="global" /></Label>
            <Input value={settings.tagline || ""} onChange={(e) => setSettings((s) => ({ ...s, tagline: e.target.value }))} disabled={!isAdmin} data-testid="settings-tagline" />
          </div>
          {isAdmin && (
            <Button onClick={save} disabled={saving} data-testid="settings-save-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
              {saving ? "Saving..." : "Save changes"}
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-6 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="font-display font-semibold text-[#252221] mb-4">Your Account</div>
        <div className="grid grid-cols-2 gap-y-3 text-sm">
          <div className="text-muted-foreground">Name</div><div className="text-[#252221]">{user?.full_name}</div>
          <div className="text-muted-foreground">Email</div><div className="text-[#252221]">{user?.email}</div>
          <div className="text-muted-foreground">Role</div><div className="text-[#252221]">{user?.role_label}</div>
          <div className="text-muted-foreground">Title</div><div className="text-[#252221]">{user?.title || "—"}</div>
        </div>
      </Card>
    </div>
  );
}
